// src/utils/formatters.js

export const formatDate = (date, format = 'DD/MM/YYYY') => {
    if (!date) return '-';
    const d = new Date(date);
    if (isNaN(d.getTime())) return '-';
    if (format === 'DD/MM/YYYY') return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
    if (format === 'YYYY-MM-DD') return d.toISOString().split('T')[0];
    if (format === 'datetime') return `${formatDate(date)} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
    if (format === 'relative') return formatRelative(d);
    return d.toLocaleDateString('it-IT');
};

export const formatRelative = (date) => {
    if (!date) return '-';
    const d = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(d.getTime())) return '-';
    const diff = Date.now() - d.getTime();
    const minutes = Math.floor(diff / 60_000);
    const hours = Math.floor(diff / 3_600_000);
    const days = Math.floor(diff / 86_400_000);
    if (minutes < 1) return 'Adesso';
    if (minutes < 60) return `${minutes} min fa`;
    if (hours < 24) return `${hours} or${hours === 1 ? 'a' : 'e'} fa`;
    if (days < 7) return `${days} giorn${days === 1 ? 'o' : 'i'} fa`;
    return formatDate(d);
};

// ==================== CURRENCY CONFIG ====================
// ✅ Tutte le valute Europa + Africa Occidentale

const CURRENCY_CONFIG = {
    // Europa
    EUR: { symbol: '€', position: 'after', locale: 'it-IT', decimals: 2 },
    GBP: { symbol: '£', position: 'before', locale: 'en-GB', decimals: 2 },
    USD: { symbol: '$', position: 'before', locale: 'en-US', decimals: 2 },

    // Africa Occidentale — UEMOA (XOF)
    XOF: { symbol: 'CFA', position: 'after', locale: 'fr-SN', decimals: 0 },

    // Guinea
    GNF: { symbol: 'FG', position: 'after', locale: 'fr-GN', decimals: 0 },

    // Nigeria
    NGN: { symbol: '₦', position: 'before', locale: 'en-NG', decimals: 0 },

    // Ghana
    GHS: { symbol: 'GH₵', position: 'before', locale: 'en-GH', decimals: 2 },

    // Afrique Centrale — CEMAC (XAF)
    XAF: { symbol: 'FCFA', position: 'after', locale: 'fr-CM', decimals: 0 },

    // Sierra Leone
    SLL: { symbol: 'Le', position: 'before', locale: 'en-SL', decimals: 0 },

    // Liberia
    LRD: { symbol: 'L$', position: 'before', locale: 'en-LR', decimals: 2 },

    // Gambia
    GMD: { symbol: 'D', position: 'after', locale: 'en-GM', decimals: 2 },

    // Mauritanie
    MRU: { symbol: 'UM', position: 'after', locale: 'ar-MR', decimals: 0 },

    // Cabo Verde
    CVE: { symbol: '$', position: 'after', locale: 'pt-CV', decimals: 2 },

    // Guinée-Bissau (usa XOF)
    // Già coperto da XOF
};

export const formatCurrency = (amount, currency = 'EUR') => {
    if (amount === undefined || amount === null) return '-';
    const num = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (isNaN(num)) return '-';

    const config = CURRENCY_CONFIG[currency] || {
        symbol: currency, position: 'after', locale: 'en-US', decimals: 0,
    };

    const formatted = num.toLocaleString(config.locale, {
        minimumFractionDigits: config.decimals,
        maximumFractionDigits: config.decimals,
    });

    return config.position === 'before'
        ? `${config.symbol}${formatted}`
        : `${formatted} ${config.symbol}`;
};

export const formatPercentage = (value, decimals = 1) => {
    if (value === undefined || value === null) return '-';
    const num = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(num)) return '-';
    return `${num.toFixed(decimals)}%`;
};

export const formatNumber = (number, decimals = 0) => {
    if (number === undefined || number === null) return '-';
    const num = typeof number === 'string' ? parseFloat(number) : number;
    if (isNaN(num)) return '-';
    return num.toLocaleString('it-IT', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
    });
};

export const truncateText = (text, maxLength = 50) => {
    if (!text) return '';
    return text.length <= maxLength ? text : `${text.substring(0, maxLength)}...`;
};

export const slugify = (text) => {
    if (!text) return '';
    return text.toString().toLowerCase()
        .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
};

export const generateCode = (prefix = '', length = 6) => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    const code = Array.from({ length }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
    return prefix ? `${prefix}-${code}` : code;
};

export const calculateTotalWithTax = (amount, taxRate = 0) => {
    const num = typeof amount === 'string' ? parseFloat(amount) : amount;
    const rate = typeof taxRate === 'string' ? parseFloat(taxRate) : taxRate;
    if (isNaN(num)) return 0;
    return num + (num * rate / 100);
};

export const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};

export const formatDuration = (minutes) => {
    if (!minutes) return '-';
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    if (h === 0) return `${m} min`;
    if (m === 0) return `${h} h`;
    return `${h} h ${m} min`;
};

export const capitalize = (string) => {
    if (!string) return '';
    return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
};

export const budgetPercentage = (actualCost, estimatedBudget) => {
    if (!estimatedBudget || estimatedBudget <= 0) return 0;
    return Math.min(100, Math.round((actualCost / estimatedBudget) * 100));
};

export const budgetColor = (pct) => {
    if (pct >= 100) return 'red';
    if (pct >= 90) return 'orange';
    if (pct >= 70) return 'yellow';
    return 'green';
};